import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function AdminLeads() {
    const [data, setData] = useState(null);
    async function load() {
        try {
            const r = await api.get("/admin/leads/overview");
            setData(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }
    useEffect(() => { load(); }, []);

    return (
        <AdminShell>
            <div className="mb-6 flex items-center justify-between" data-testid="admin-leads-header">
                <div>
                    <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Leads</h1>
                    <p className="text-[#64748B] mt-1 text-[14px]">Aggregate view — read-only. Doctors manage leads on their own dashboards.</p>
                </div>
                <button onClick={load} className="ddi-btn-outline"><RefreshCw size={13} /> Refresh</button>
            </div>

            {!data ? (
                <div className="text-[#64748B]">Loading…</div>
            ) : (
                <>
                    <div className="mb-6">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">Total leads captured</div>
                        <div className="text-4xl font-bold text-[#0F172A] mt-1" data-testid="admin-leads-total">{data.total}</div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card className="border-[#E5E9F2] ddi-shadow-sm">
                            <CardHeader className="pb-3"><CardTitle className="text-[15px]">Top doctors by lead count</CardTitle></CardHeader>
                            <CardContent>
                                {data.top_doctors.length === 0 ? (
                                    <div className="py-6 text-[13px] text-[#64748B] text-center">No leads captured yet.</div>
                                ) : (
                                    <ul className="divide-y divide-[#EEF1F6]" data-testid="admin-leads-top">
                                        {data.top_doctors.map((row) => (
                                            <li key={row.doctor?.id} className="py-3 flex items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13px] font-semibold text-[#0F172A] truncate">{row.doctor?.full_name || "—"}</div>
                                                    <div className="text-[11px] text-[#94A3B8]">{row.doctor?.specialization}</div>
                                                </div>
                                                <span className="ddi-chip ddi-chip-primary">{row.count} lead{row.count === 1 ? "" : "s"}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="border-[#E5E9F2] ddi-shadow-sm">
                            <CardHeader className="pb-3"><CardTitle className="text-[15px]">Recent leads</CardTitle></CardHeader>
                            <CardContent>
                                {data.recent.length === 0 ? (
                                    <div className="py-6 text-[13px] text-[#64748B] text-center">No recent leads.</div>
                                ) : (
                                    <ul className="divide-y divide-[#EEF1F6] max-h-[500px] overflow-y-auto" data-testid="admin-leads-recent">
                                        {data.recent.map((l) => (
                                            <li key={l.id} className="py-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[13px] font-semibold text-[#0F172A]">{l.name || l.phone || l.email || "Lead"}</span>
                                                    <span className="text-[10px] text-[#94A3B8]">{l.created_at ? new Date(l.created_at).toLocaleString() : ""}</span>
                                                </div>
                                                <div className="text-[11px] text-[#64748B]">
                                                    for Dr. {l.doctor?.full_name || "—"}
                                                    {l.message ? <> · <span className="italic">"{(l.message || "").slice(0, 100)}"</span></> : null}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}
        </AdminShell>
    );
}
